import { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { AgentMessage } from '../../Core/Types/bizagent';
import { createSession, getSession, sendMessageAndFetchResponseAsStream } from '../../Core/Services/bizagent';
import BizAgentMessageComponent from './BizAgentMessage';
import BizAgentInput from './BizAgentInput';
import './BizAgentWindow.scss';
import { useTranslation } from 'react-i18next';

interface BizAgentWindowProps {
	sessionId: string | null;
	configSystemName: string;
	onClose: () => void;
	onSessionCreate: (sessionId: string) => void;
	onSessionClear: () => void;
	data?: string; // Optional structured data to send with messages
	onDataChange?: (newData: string) => void; // Callback when data changes
	inlineMode?: boolean;
	suggestedMessages?: string[];
}

const parseSuggestionPages = (value: string): string[][] => {
	const pages: string[][] = [];
	let page: string[] = [];

	value.split(/\r?\n/).forEach((line) => {
		const suggestion = line.trim();
		if (suggestion) {
			page.push(suggestion);
		} else if (page.length) {
			pages.push(page);
			page = [];
		}
	});

	if (page.length) pages.push(page);
	return pages;
};

const ACTIVE_STREAM_IDLE_WINDOW_MS = 1000;
const ACTIVE_STREAM_FRAME_MS = 16;

const BizAgentWindow = ({
	sessionId,
	configSystemName,
	onClose,
	onSessionCreate,
	onSessionClear,
	data,
	inlineMode,
	suggestedMessages,
}: BizAgentWindowProps) => {
	const { t } = useTranslation();
	const [agentName, setAgentName] = useState<string>('');
	const [welcomeMessage, setWelcomeMessage] = useState<string>('');
	const [inputPlaceholder, setInputPlaceholder] = useState<string>('');
	const [showHeader, setShowHeader] = useState(true);
	const [showWelcomeMessage, setShowWelcomeMessage] = useState(true);
	const [messages, setMessages] = useState<AgentMessage[]>([]);
	const [suggestionPages, setSuggestionPages] = useState<string[][]>(suggestedMessages?.length ? [suggestedMessages] : []);
	const [suggestionPageIndex, setSuggestionPageIndex] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [currentSessionId, setCurrentSessionId] = useState<string | null>(sessionId);
	const [error, setError] = useState<string | null>(null);
	const [showSuggestedMessages, setShowSuggestedMessages] = useState(true);
	const [currentData, setCurrentData] = useState<string | undefined>(data);
	const rootRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLTextAreaElement>(null);
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const shouldScrollToTopOnExpandRef = useRef(false);
	const skipNextScrollToBottomRef = useRef(false);
	const doneBufferRef = useRef<string | null>(null);
	const pendingResponseBufferRef = useRef('');
	const visibleResponseBufferRef = useRef('');
	const streamingMessageRef = useRef<AgentMessage | null>(null);
	const responseFlushFrameRef = useRef<number | null>(null);
	const scrollFrameRef = useRef<number | null>(null);
	const lastRenderTimestampRef = useRef<number | null>(null);
	const lastChunkTimestampRef = useRef<number | null>(null);
	const streamCompletedRef = useRef(false);

	useEffect(() => {
		if (skipNextScrollToBottomRef.current) {
			skipNextScrollToBottomRef.current = false;
			return;
		}

		requestScrollToBottom();
	}, [messages.length]);

	useLayoutEffect(() => {
		if (!inlineMode || messages.length === 0 || !shouldScrollToTopOnExpandRef.current) {
			return;
		}

		shouldScrollToTopOnExpandRef.current = false;
		skipNextScrollToBottomRef.current = true;

		requestAnimationFrame(() => {
			rootRef.current?.scrollIntoView({
				block: 'start',
				behavior: 'smooth',
			});
		});
	}, [inlineMode, messages.length]);

	useEffect(() => {
		if (currentSessionId) {
			loadSession();
		} else {
			initializeSession();
		}
	}, [currentSessionId]);

	// Watch for data changes and update currentData
	useEffect(() => {
		if (data !== currentData) {
			setCurrentData(data);
		}
	}, [data, currentData]);

	useEffect(() => {
		return () => {
			cancelScheduledFrames();
		};
	}, []);

	const cancelScheduledFrames = () => {
		if (responseFlushFrameRef.current !== null) {
			cancelAnimationFrame(responseFlushFrameRef.current);
			responseFlushFrameRef.current = null;
		}

		if (scrollFrameRef.current !== null) {
			cancelAnimationFrame(scrollFrameRef.current);
			scrollFrameRef.current = null;
		}
	};

	const resetStreamingState = () => {
		cancelScheduledFrames();
		doneBufferRef.current = null;
		pendingResponseBufferRef.current = '';
		visibleResponseBufferRef.current = '';
		streamingMessageRef.current = null;
		lastRenderTimestampRef.current = null;
		lastChunkTimestampRef.current = null;
		streamCompletedRef.current = false;
	};

	const createStreamingMessage = (): AgentMessage => ({
		id: Date.now(),
		sessionId: 0,
		message: '',
		messageType: 'BOT',
		response: '',
		createdDate: new Date().toISOString(),
		isDeleted: false,
		streaming: true,
	});

	const getOrCreateStreamingMessage = () => {
		if (!streamingMessageRef.current) {
			streamingMessageRef.current = createStreamingMessage();
		}

		return streamingMessageRef.current;
	};

	const requestScrollToBottom = () => {
		if (scrollFrameRef.current !== null) {
			return;
		}

		scrollFrameRef.current = requestAnimationFrame(() => {
			scrollFrameRef.current = null;
			scrollToBottom();
		});
	};

	const syncStreamingMessage = (response: string) => {
		setMessages((prev) => {
			const newList = [...prev];
			const existingIndex = newList.findIndex((message) => message.messageType === 'BOT' && message.streaming);
			const streamingMessage = getOrCreateStreamingMessage();

			streamingMessage.response = response;

			if (existingIndex === -1) {
				newList.push({ ...streamingMessage });
				return newList;
			}

			newList[existingIndex] = {
				...newList[existingIndex],
				...streamingMessage,
				response,
			};

			return newList;
		});

		requestScrollToBottom();
	};

	const getRenderBudget = (timestamp: number) => {
		const previousTimestamp = lastRenderTimestampRef.current;
		lastRenderTimestampRef.current = timestamp;

		const elapsed = previousTimestamp ? Math.max(1, timestamp - previousTimestamp) : 16;
		const elapsedFrames = Math.max(1, Math.round(elapsed / 16));
		const backlog = pendingResponseBufferRef.current.length;
		let budget = elapsedFrames * 8;

		if (backlog > 400) {
			budget = Math.max(budget, Math.ceil(backlog * 0.45));
		} else if (backlog > 200) {
			budget = Math.max(budget, Math.ceil(backlog * 0.3));
		} else if (backlog > 80) {
			budget = Math.max(budget, Math.ceil(backlog * 0.18));
		}

		return Math.max(1, budget);
	};

	const getPendingReserve = (idleTime: number) => {
		if (streamCompletedRef.current || idleTime >= ACTIVE_STREAM_IDLE_WINDOW_MS) {
			return 0;
		}

		const pendingLength = pendingResponseBufferRef.current.length;
		const minimumReserve = visibleResponseBufferRef.current.length === 0 ? 48 : 32;
		const adaptiveReserve = Math.min(180, Math.ceil(pendingLength * 0.35));

		return Math.max(minimumReserve, adaptiveReserve);
	};

	const getIdleTime = (timestamp: number) => {
		if (lastChunkTimestampRef.current === null) {
			return Number.POSITIVE_INFINITY;
		}

		return timestamp - lastChunkTimestampRef.current;
	};

	const getSmoothedRenderBudget = (renderBudget: number, idleTime: number) => {
		if (streamCompletedRef.current || idleTime >= ACTIVE_STREAM_IDLE_WINDOW_MS) {
			return renderBudget;
		}

		const remainingWindowMs = Math.max(ACTIVE_STREAM_FRAME_MS, ACTIVE_STREAM_IDLE_WINDOW_MS - idleTime);
		const remainingFrames = Math.max(1, Math.ceil(remainingWindowMs / ACTIVE_STREAM_FRAME_MS));
		const pendingLength = pendingResponseBufferRef.current.length;
		const smoothedBudget = Math.max(1, Math.ceil(pendingLength / remainingFrames));

		return Math.max(1, Math.min(renderBudget, smoothedBudget));
	};

	const flushPendingResponse = (timestamp: number) => {
		responseFlushFrameRef.current = null;

		if (!pendingResponseBufferRef.current.length) {
			lastRenderTimestampRef.current = null;
			if (streamCompletedRef.current && doneBufferRef.current !== null) {
				finalizeDoneMessage();
			}
			return;
		}

		const idleTime = getIdleTime(timestamp);
		const pendingReserve = getPendingReserve(idleTime);
		const shouldHoldBack = !streamCompletedRef.current && idleTime < ACTIVE_STREAM_IDLE_WINDOW_MS;
		const availableToRender = shouldHoldBack
			? Math.max(0, pendingResponseBufferRef.current.length - pendingReserve)
			: pendingResponseBufferRef.current.length;

		if (availableToRender === 0) {
			responseFlushFrameRef.current = requestAnimationFrame(flushPendingResponse);
			return;
		}

		const renderBudget = getSmoothedRenderBudget(getRenderBudget(timestamp), idleTime);
		const nextVisibleChunk = pendingResponseBufferRef.current.slice(0, Math.min(renderBudget, availableToRender));
		pendingResponseBufferRef.current = pendingResponseBufferRef.current.slice(nextVisibleChunk.length);
		visibleResponseBufferRef.current += nextVisibleChunk;

		syncStreamingMessage(visibleResponseBufferRef.current);

		if (pendingResponseBufferRef.current.length) {
			responseFlushFrameRef.current = requestAnimationFrame(flushPendingResponse);
		} else if (streamCompletedRef.current && doneBufferRef.current !== null) {
			finalizeDoneMessage();
		}
	};

	const scheduleResponseFlush = () => {
		if (responseFlushFrameRef.current !== null) {
			return;
		}

		responseFlushFrameRef.current = requestAnimationFrame(flushPendingResponse);
	};

	const queueResponseChunk = (chunk: string) => {
		if (!chunk) {
			return;
		}

		lastChunkTimestampRef.current = performance.now();
		pendingResponseBufferRef.current += chunk;
		scheduleResponseFlush();
	};

	const scrollToBottom = () => {
		if (messagesEndRef.current) {
			// Get the messages container (parent of messagesEndRef)
			const messagesContainer = messagesEndRef.current.closest('[data-messages-container]');
			if (messagesContainer) {
				if (inlineMode) {
					const agentWidget = document.getElementById('bizagentwidget');
					if (!agentWidget) return;

					const rect = agentWidget.getBoundingClientRect();
					const offset = window.scrollY + rect.bottom - window.innerHeight;

					window.scrollTo({ top: offset, behavior: 'smooth' });
				} else {
					// Scroll within the container only, not the whole page
					messagesContainer.scrollTop = messagesContainer.scrollHeight;
				}
			} else {
				console.log('messagesContainer not found');
			}
		}
	};

	const messageStreaming = messages.some((msg) => msg.streaming);
	console.log('Message streaming:', messageStreaming);
	console.log('Messages:', messages);

	const initializeSession = async () => {
		try {
			console.log('Initializing session with config:', configSystemName);
			setIsLoading(true);
			const session = await createSession(configSystemName);
			if (session) {
				console.log('Created new session:', session);
				setCurrentSessionId(session.sessionId);
				onSessionCreate(session.sessionId);
				setMessages([]);
			}
			setError(null);
		} catch (err) {
			setError(t('BIZAGENT.ERROR_INIT_SESSION'));
			console.error('Failed to create session:', err);
		} finally {
			setIsLoading(false);
		}
	};

	const loadSession = async () => {
		if (!currentSessionId) return;

		try {
			setIsLoading(true);
			resetStreamingState();
			const session = await getSession(currentSessionId);
			if (session) {
				setMessages(session.messages || []);

				if (session.configSuggestions) {
					setSuggestionPages(parseSuggestionPages(session.configSuggestions));
					setSuggestionPageIndex(0);
				}

				if (session.configAgentName) {
					setAgentName(session.configAgentName);
				}

				if (session.configWelcomeMessage) {
					setWelcomeMessage(session.configWelcomeMessage);
				}

				setInputPlaceholder(session.configInputPlaceholder || '');
				setShowHeader(session.configShowHeader ?? true);
				setShowWelcomeMessage(session.configShowWelcomeMessage ?? true);

				// setTimeout(() => {
				// 	if (inputRef.current && !inputRef.current.disabled && messages.length > 0) {
				// 		inputRef.current.focus();
				// 		// Also ensure cursor is at the end if there's any text
				// 		const length = inputRef.current.value.length;
				// 		inputRef.current.setSelectionRange(length, length);
				// 	}
				// }, 150);
			}
			setError(null);
		} catch (err) {
			setError(t('BIZAGENT.ERROR_LOAD_HISTORY'));
			console.error('Failed to load session:', err);
		} finally {
			setIsLoading(false);
		}
	};

	const finalizeDoneMessage = () => {
		if (doneBufferRef.current === null) return;
		if (pendingResponseBufferRef.current.length > 0) return;

		const finalMessageJson = doneBufferRef.current;
		resetStreamingState();

		try {
			const finalMessage = JSON.parse(finalMessageJson) as AgentMessage;
			setMessages((prev) => {
				const listWTempMessageRemoved = prev.filter((m) => !(m.messageType === 'BOT' && m.streaming));
				return [...listWTempMessageRemoved, finalMessage];
			});
			setIsLoading(false);
			requestScrollToBottom();
		} catch (e) {
			setIsLoading(false);
			console.error('Failed to parse final message JSON:', e, finalMessageJson);
		}
	};

	const addResponseChunk = (chunk: string) => {
		// If we're already buffering [DONE]: content, append to buffer
		if (doneBufferRef.current !== null) {
			doneBufferRef.current += chunk;
			return;
		}

		// Check if this chunk contains the [DONE]: marker
		if (chunk.includes('[DONE]:')) {
			// Split into content before and after the [DONE]: marker
			const parts = chunk.split('[DONE]:', 2);
			const beforeDone = parts[0] || '';
			const afterDone = parts[1] || '';

			// First, append any content before the marker to the streaming message
			if (beforeDone) {
				queueResponseChunk(beforeDone);
			}

			// Start buffering the final [DONE]: payload (JSON) for later parsing
			doneBufferRef.current = afterDone;
			return;
		}

		queueResponseChunk(chunk);
	};

	const handleSuggestedMessageClick = useCallback(
		async (message: string) => {
			setShowSuggestedMessages(false);
			await handleSendMessage(message);
		},
		[currentSessionId]
	);

	const handleSendMessage = async (message: string) => {
		console.log('Sending message with session ID:', currentSessionId);
		if (!currentSessionId || !message.trim()) return;
		if (inlineMode && messages.length === 0) {
			shouldScrollToTopOnExpandRef.current = true;
		}

		// Add user message immediately for better UX
		const userMessage: AgentMessage = {
			id: Date.now(), // Temporary ID
			sessionId: 0,
			message,
			messageType: 'USER',
			createdDate: new Date().toISOString(),
			isDeleted: false,
		};

		setMessages((prev) => [...prev, userMessage]);
		resetStreamingState();
		setIsLoading(true);
		setError(null);

		try {
			// const response = await sendMessage({
			// 	sessionId: currentSessionId,
			// 	message,
			// });
			await sendMessageAndFetchResponseAsStream(
				{
					sessionId: currentSessionId,
					message,
					data: currentData, // Include structured data if available
				},
				(chunk) => {
					// Handle each chunk of the streamed response
					console.log('Received chunk:', chunk);
					addResponseChunk(chunk);
				},
				() => {
					// Handle completion - finalize any buffered [DONE]: message
					console.log('Streaming completed');
					streamCompletedRef.current = true;
					scheduleResponseFlush();
					finalizeDoneMessage();
				}
			);

			// Remove the temporary user message and add the real bot response
			// setMessages((prev) => {
			// 	// const filtered = prev.filter(m => m.id !== userMessage.id);
			// 	// return response ? [...filtered, response] : filtered;
			// 	return response ? [...prev, response] : prev;
			// });
		} catch (err) {
			resetStreamingState();
			setError(t('BIZAGENT.ERROR_SEND_MESSAGE'));
			console.error('Failed to send message:', err);
			// Remove the temporary message on error
			setMessages((prev) => prev.filter((m) => m.id !== userMessage.id && !(m.messageType === 'BOT' && m.streaming)));
			setIsLoading(false);
		}
	};

	const handleClearChat = () => {
		resetStreamingState();
		setMessages([]);
		setCurrentSessionId(null);
		setShowSuggestedMessages(true);
		onSessionClear();
		initializeSession();
	};

	const hasSuggestionsToShow = showSuggestedMessages && suggestionPages.length > 0 && messages.length === 0;
	const activeSuggestions = suggestionPages[suggestionPageIndex] || [];
	const translatedInputPlaceholder = inputPlaceholder
		? t(inputPlaceholder, { defaultValue: inputPlaceholder })
		: undefined;
	const wrapperClasses = `biz-agent-window ${inlineMode ? 'inline-mode' : 'widget-mode'} ${hasSuggestionsToShow ? 'state-init' : ''}`;
	const suggestionContent = hasSuggestionsToShow ? (
		<div className='biz-agent-suggested-messages'>
			<p className='biz-agent-suggested-title'>{t('BIZAGENT.SUGGESTED_TITLE')}</p>
			<div className='biz-agent-suggestions'>
				{activeSuggestions.map((message, index) => (
					<button
						key={index}
						className='biz-agent-suggested-btn'
						onClick={() => handleSuggestedMessageClick(message)}
						disabled={isLoading}
					>
						{message}
					</button>
				))}
			</div>
			{suggestionPages.length > 1 && (
				<div className='biz-agent-suggestion-pagination'>
					<div className='biz-agent-suggestion-dots' aria-label={t('BIZAGENT.SUGGESTION_PAGE')}>
						{suggestionPages.map((_, index) => (
							<button
								key={index}
								className={index === suggestionPageIndex ? 'active' : ''}
								onClick={() => setSuggestionPageIndex(index)}
								aria-label={`${t('BIZAGENT.SUGGESTION_PAGE')} ${index + 1}`}
							/>
						))}
					</div>
					<button
						className='biz-agent-more-suggestions-btn'
						onClick={() => setSuggestionPageIndex((suggestionPageIndex + 1) % suggestionPages.length)}
					>
						{t('BIZAGENT.MORE_SUGGESTIONS')}
					</button>
				</div>
			)}
		</div>
	) : null;

	return (
		<div ref={rootRef} className={wrapperClasses}>
			{showHeader && <div className='biz-agent-header'>
				<div className='biz-agent-header-agent-name'>{agentName && <h3>{agentName}</h3>}</div>
				<div className='biz-agent-header-actions'>
					<button className='biz-agent-clear-btn' onClick={handleClearChat} title={t('BIZAGENT.CLEAR_CHAT')}>
						<i className='far fa-trash-alt' />
					</button>
					{!inlineMode && (
						<button className='biz-agent-close-btn' onClick={onClose} title={t('BIZAGENT.CLOSE_CHAT')}>
							✕
						</button>
					)}
				</div>
			</div>}

			<div className={`biz-agent-messages biz-agent-has-messages-${messages.length > 0 ? 'true' : 'false'}`} data-messages-container>
				{showWelcomeMessage && messages.length === 0 && !isLoading && (
					<div className='biz-agent-welcome'>
						<p>{welcomeMessage || t('BIZAGENT.WELCOME_MESSAGE')}</p>
					</div>
				)}

				{!inlineMode && suggestionContent}

				{error && (
					<div className='biz-agent-error'>
						<p>{error}</p>
						<button onClick={() => setError(null)}>{t('BIZAGENT.DISMISS')}</button>
					</div>
				)}

				{messages.map((message, index) => (
					<BizAgentMessageComponent key={`${message.id}-${index}`} message={message} />
				))}

				{isLoading && !messageStreaming && (
					<div className='biz-agent-loading'>
						<div className='biz-agent-typing'>
							<span></span>
							<span></span>
							<span></span>
						</div>
					</div>
				)}

				<div ref={messagesEndRef} />
			</div>

			<BizAgentInput
				inputRef={inputRef}
				onSendMessage={handleSendMessage}
				disabled={isLoading || !currentSessionId}
				placeholder={translatedInputPlaceholder}
			/>
			{inlineMode && suggestionContent && (
				<div className='biz-agent-messages biz-agent-inline-suggestions'>{suggestionContent}</div>
			)}
		</div>
	);
};

export default BizAgentWindow;
